'use client';

import { useOverlayTriggerState } from '@react-stately/overlays';
import type { MouseEvent, ReactNode} from 'react';
import { useState } from 'react';
import { useAuth } from 'react-oidc-context';
import styled from 'styled-components';

import { Bookmark, Share, UnfilledBookmark } from '@zoomit/icons';
import { Sheet } from '@zoomit/mobile-sheet';
import type { PopoverPlacement} from '@zoomit/primitives';
import { Box, Flex, IconButton, Popover, Portal, Spacing } from '@zoomit/primitives';
import { Button, cssVar, theme, Typography } from '@zoomit/zoomit-ds';

import { isMobile } from '../../../constants/media-query';
import { onShareArticle } from '../../../helpers/events';
import { shareHandler } from '../../../helpers/share-handler';
import { useArticleBookmarks, useArticleBookmarkStatus } from '../../../hooks/useArticleBookmarks';
import type { BookmarksModalState } from '../../../types/articles';
import { ArticleBookmarkHeader, ArticleBookmarksModal } from '../../bookmark';
import ArticleDesktopBookmarkModal from '../../bookmark/ArticleDesktopBookmarkModal';
import { SharePopover } from '../../shared';
import UnauthorizedModal from '../../shared/UnauthorizedModal';

type Props = {
  articleId: string;
  url: string;
  title: string;
  onBookmarksRefetch?: () => void;
  hasBookmark?: boolean;
};

type DesktopMoreArticleProps = {
  placement: PopoverPlacement;
  children: ReactNode;
};

const MoreArticleOptions = (props: Props) => {
  const { title, url, articleId, onBookmarksRefetch, hasBookmark = true } = props;
  const [isMoreOptionsOpen, setIsMoreOptionsOpen] = useState(false);
  const [bookmarkModal, setBookmarkModal] = useState<BookmarksModalState>(null);
  const { isAuthenticated } = useAuth();

  const unauthorizedModal = useOverlayTriggerState({});

  const { data: bookmarksList } = useArticleBookmarks({ enabled: isAuthenticated });

  const {
    data: articleBookmarkStatus,
    refetch: refetchBookmarkStatus,
    isFetched: isBookmarkStatusFetched,
  } = useArticleBookmarkStatus({
    articleId,
    enabled: false,
  });

  const [showShare, setShowShare] = useState(false);

  const toggleMoreOptions = async (e?: any) => {
    e?.stopPropagation();
    if (!isBookmarkStatusFetched) {
      await refetchBookmarkStatus();
    }
    setIsMoreOptionsOpen((preState) => !preState);
  };

  const onBookmarkClick = (e: MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isAuthenticated) {
      unauthorizedModal.open();
    } else {
      setBookmarkModal('bookmark-list');
    }
    if (!isMobile) {
      setIsMoreOptionsOpen(false);
    }
  };

  const closeBookmarkModal = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    onBookmarksRefetch && onBookmarksRefetch();
    setBookmarkModal(null);
    setIsMoreOptionsOpen(false);
  };

  const DesktopMoreArticleOptions = (props: DesktopMoreArticleProps) => {
    const { children, placement } = props;

    return (
      <>
        <SharePopover
          url={url}
          title={title}
          placement={placement}
          offset={6}
          showShare={showShare}
          setShowShare={(show) => {
            setIsMoreOptionsOpen(false);
            setShowShare(false);
          }}
          onShare={(method) => onShareArticle(method)}
        >
          <Popover
            isOpen={isMoreOptionsOpen}
            setOpen={() => {
              setIsMoreOptionsOpen(false);
              setShowShare(false);
            }}
            placement={placement}
            triggerOffset={6}
            content={
              <Box backgroundColor={cssVar(theme.card)}>
                {hasBookmark && (
                  <Item
                    spacing={{ pl: 'x6', pr: 'x2', py: 'x3' }}
                    alignItems="center"
                    gap={1}
                    onClick={onBookmarkClick}
                  >
                    <IconButton>
                      {articleBookmarkStatus && articleBookmarkStatus.length > 0 ? (
                        <Bookmark className="fill-black3" />
                      ) : (
                        <UnfilledBookmark />
                      )}
                    </IconButton>
                    <Typography variant="subhead" textColor={cssVar(theme.grey1)}>
                      بوکمارک
                    </Typography>
                  </Item>
                )}

                <Item
                  spacing={{ pl: 'x6', pr: 'x2', py: 'x3' }}
                  alignItems="center"
                  gap={1}
                  onClick={() => {
                    setShowShare(true);
                    setIsMoreOptionsOpen(false);
                  }}
                >
                  <IconButton>
                    <Share size="md" />
                  </IconButton>
                  <Typography variant="subhead" textColor={cssVar(theme.grey1)}>
                    اشتراک گذاری
                  </Typography>
                </Item>
              </Box>
            }
          >
            <div style={{ pointerEvents: showShare || isMoreOptionsOpen ? 'none' : 'inherit' }}>
              {children}
            </div>
          </Popover>
        </SharePopover>
        {bookmarkModal !== null && (
          <Portal>
            <ArticleDesktopBookmarkModal
              modal={bookmarkModal}
              setModal={(value) => setBookmarkModal(value)}
              articleId={articleId}
              articleBookmarkStatus={articleBookmarkStatus}
            />
          </Portal>
        )}
        <UnauthorizedModal isOpen={unauthorizedModal.isOpen} onClose={() => unauthorizedModal.close()} />
      </>
    );
  };

  const MobileMoreArticleOptions = () => (
    <>
      <Flex spacing={{ px: 'x10', pb: 'x5' }} flexDirection="column" alignItems="center" gap={Spacing.x4}>
        {hasBookmark && (
          <Flex style={{ width: '100%', cursor: 'pointer' }} gap={1.3} onClick={onBookmarkClick}>
            {articleBookmarkStatus && articleBookmarkStatus.length > 0 ? (
              <Bookmark className="fill-black3" />
            ) : (
              <UnfilledBookmark />
            )}
            <Typography textColor={cssVar(theme.grey1)}>بوکمارک</Typography>
          </Flex>
        )}
        <Flex
          className="w-full cursor-pointer"
          gap={1.3}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            shareHandler({ title: 'Zoomit', text: title, url, onShare: () => onShareArticle('Mobile') });
          }}
        >
          <Share />
          <Typography textColor={cssVar(theme.grey1)}>اشتراک گذاری</Typography>
        </Flex>
      </Flex>
      <Box spacing={{ px: 'x10', py: 'x5' }} backgroundColor={cssVar(theme.grey7)}>
        <Button variant="outline" size="full width" onClick={closeBookmarkModal}>
          بستن
        </Button>
      </Box>
      {
        <Portal>
          <Sheet
            isOpen={!!bookmarkModal}
            onClose={() => setBookmarkModal(null)}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <Sheet.Container
              style={{
                backgroundColor: cssVar(theme.background2),
                height: bookmarkModal === 'bookmark-list' ? '90%' : 'unset',
                position: 'absolute',
              }}
            >
              <Sheet.Header>
                <Sheet.CloseIndicator />
                <BookmarkListHeaderWrapper
                  className={bookmarkModal ?? ''}
                  justifyContent="center"
                  alignItems="center"
                  spacing={{ pb: 1 }}
                >
                  <ArticleBookmarkHeader
                    closeModal={() => setBookmarkModal(null)}
                    title="مدیریت بوک‌مارک‌ها"
                  />
                </BookmarkListHeaderWrapper>
              </Sheet.Header>
              <Flex className={'w-full'} spacing={{ px: Spacing.x11, pt: Spacing.x7 }}>
                <ArticleBookmarksModal
                  bookmarkedItems={articleBookmarkStatus || []}
                  articleId={articleId}
                  bookmarkList={bookmarksList || []}
                  modal={bookmarkModal}
                  closeModal={() => {
                    setBookmarkModal(null);
                    setIsMoreOptionsOpen(false);
                  }}
                  openDeleteModal={() => setBookmarkModal('remove-bookmark')}
                  openListModal={() => setBookmarkModal('bookmark-list')}
                />
              </Flex>
            </Sheet.Container>
            <Sheet.Backdrop />
          </Sheet>
        </Portal>
      }
      <UnauthorizedModal isOpen={unauthorizedModal.isOpen} onClose={() => unauthorizedModal.close()} />
    </>
  );

  return {
    DesktopMoreArticleOptions,
    MobileMoreArticleOptions,
    toggleMoreOptions,
    isMoreOptionsOpen,
  };
};

export default MoreArticleOptions;

const Item = styled(Flex)`
  cursor: pointer;
`;

const BookmarkListHeaderWrapper = styled(Flex)`
  width: 100%;
  height: 100%;

  &.remove-bookmark {
    justify-content: flex-start;
    padding-right: 6.4rem;
  }
`;
